import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  Save, 
  ArrowBack, 
  Language,
  Settings,
  Assignment,
  People,
  Analytics,
  ExpandMore
} from '@mui/icons-material';
import api from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';

const AddSite = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    // Required fields
    rootdomain: '',
    website_url: '',
    
    // Optional CSV fields
    account_name: '',
    account_status: '',
    priority: '',
    total_spend: '',
    doctor_name: '',
    account_id: '',
    account_id_unique: '',
    item_id: '',
    assigned_cs: '',
    strategist: '',
    seo_shirt_size: '',
    seo_hours: 0,
    ga4_id: '',
    ga4_email: '',
    ga4_account_name: '',
    ga4_property_id: '',
    gtm_id: '',
    gtm_email: '',
    gsc_domain: '',
    gsc_prefix: '',
    verified_gsc_prefix: '',
    gsc_email: '',
    br_date: '',
    seo_master_sheet: '',
    google_drive_link: '',
    csv_account_id: '',
    access_id: '',
    strategy_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'seo_hours' ? (value === '' ? 0 : parseInt(value) || 0) : value
    }));
  };

  const [addConfirmOpen, setAddConfirmOpen] = useState(false);

  const statusOptions = ['Live', 'Collections', 'Paused', 'Setup'];
  const priorityOptions = ['Healthy', 'Referral', 'Maintain'];
  const sizeOptions = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.rootdomain.trim()) {
      setError('Root domain is required');
      return;
    }

    if (!formData.website_url.trim()) {
      setError('Website URL is required');
      return;
    }

    if (!validateUrl(formData.website_url)) {
      setError('Please enter a valid URL');
      return;
    }

    setAddConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setAddConfirmOpen(false);
    setLoading(true);

    try {
      await api.post('/sites/', formData);
      setSuccess('Site added successfully!');
      
      setTimeout(() => {
        navigate('/sites');
      }, 1500);
    } catch (error) {
      console.error('Error adding site:', error);
      
      // Handle different error response formats
      let errorMessage = 'Failed to add site';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Handle validation errors array
          errorMessage = errorData.detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return `${err.loc?.join('.') || 'Field'}: ${err.msg}`;
            return JSON.stringify(err);
          }).join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/sites')}
            sx={{ mb: 2 }}
          >
            Back to Sites
          </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Language sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            Add New Site
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Add a new WordPress site with complete account and tracking information
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleFormSubmit}>
          <Grid container spacing={3}>
            {/* Required Basic Information */}
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title="Basic Information (Required)"
                  avatar={<Settings color="primary" />}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Root Domain"
                        name="rootdomain"
                        value={formData.rootdomain}
                        onChange={handleChange}
                        required
                        placeholder="example.com"
                        helperText="Enter the root domain without protocol"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Website URL"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        required
                        placeholder="https://example.com"
                        helperText="Complete URL including protocol"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Optional Account Details */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Assignment sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6">Account Details (Optional)</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Account Name"
                        name="account_name"
                        value={formData.account_name}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Doctor Name"
                        name="doctor_name"
                        value={formData.doctor_name}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Account Status</InputLabel>
                        <Select
                          name="account_status"
                          value={formData.account_status}
                          onChange={handleChange}
                          label="Account Status"
                        >
                          {statusOptions.map(status => (
                            <MenuItem key={status} value={status}>{status}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select
                          name="priority"
                          value={formData.priority}
                          onChange={handleChange}
                          label="Priority"
                        >
                          {priorityOptions.map(priority => (
                            <MenuItem key={priority} value={priority}>{priority}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Total Spend"
                        name="total_spend"
                        value={formData.total_spend}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Account ID"
                        name="account_id"
                        value={formData.account_id}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Item ID"
                        name="item_id"
                        value={formData.item_id}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Team & SEO */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <People sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6">Team & SEO (Optional)</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Assigned CS"
                        name="assigned_cs"
                        value={formData.assigned_cs}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Strategist"
                        name="strategist"
                        value={formData.strategist}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>SEO Shirt Size</InputLabel>
                        <Select
                          name="seo_shirt_size"
                          value={formData.seo_shirt_size}
                          onChange={handleChange}
                          label="SEO Shirt Size"
                        >
                          {sizeOptions.map(size => (
                            <MenuItem key={size} value={size}>{size}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="SEO Hours"
                        name="seo_hours"
                        type="number"
                        value={formData.seo_hours}
                        onChange={handleChange}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Analytics & Tracking */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Analytics sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6">Analytics & Tracking (Optional)</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="GA4 ID"
                        name="ga4_id"
                        value={formData.ga4_id}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="GA4 Email"
                        name="ga4_email"
                        value={formData.ga4_email}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="GA4 Account Name"
                        name="ga4_account_name"
                        value={formData.ga4_account_name}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="GA4 Property ID"
                        name="ga4_property_id"
                        value={formData.ga4_property_id}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="GTM ID"
                        name="gtm_id"
                        value={formData.gtm_id}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="GTM Email"
                        name="gtm_email"
                        value={formData.gtm_email}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="GSC Domain"
                        name="gsc_domain"
                        value={formData.gsc_domain}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="GSC Prefix"
                        name="gsc_prefix"
                        value={formData.gsc_prefix}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="GSC Email"
                        name="gsc_email"
                        value={formData.gsc_email}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Verified GSC Prefix"
                        name="verified_gsc_prefix"
                        value={formData.verified_gsc_prefix}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Links & Additional Info */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Language sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6">Links & Additional Info (Optional)</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="BR Date"
                        name="br_date"
                        value={formData.br_date}
                        onChange={handleChange}
                        placeholder="YYYY-MM-DD"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="CSV Account ID"
                        name="csv_account_id"
                        value={formData.csv_account_id}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="SEO Master Sheet"
                        name="seo_master_sheet"
                        value={formData.seo_master_sheet}
                        onChange={handleChange}
                        placeholder="https://docs.google.com/..."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Google Drive Link"
                        name="google_drive_link"
                        value={formData.google_drive_link}
                        onChange={handleChange}
                        placeholder="https://drive.google.com/..."
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Access ID"
                        name="access_id"
                        value={formData.access_id}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Strategy ID"
                        name="strategy_id"
                        value={formData.strategy_id}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Actions */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/sites')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  disabled={loading}
                  size="large"
                >
                  {loading ? 'Adding Site...' : 'Add Site'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
        </Box>
      </Container>

      <ConfirmDialog
        open={addConfirmOpen}
        title="Add New Site"
        message="Are you sure you want to add this new site?"
        onConfirm={handleSubmit}
        onCancel={() => setAddConfirmOpen(false)}
        confirmLabel="Yes, Add Site"
        cancelLabel="No"
      />
    </Box>
  );
};

export default AddSite;